// How a soldier turns on screen (2026-10-30, Devin: "it doesn't just do like an abrupt 45-degree turn, I want to see the
// smooth transition"). The sim sets sim.face outright each tick, and its march follows a flow field whose cells point at
// one of 8 neighbours, so a soldier's facing jumps in 45 degree steps and a reversal is one frame. The drawn facing
// follows it instead, display only (the sim and the server never see it): the target is eased first, so a turn builds
// up over a few frames instead of kicking, then a critically damped follower (no overshoot, no wobble at the end) turns
// toward it no faster than the class's maxRate. Ported from Sheep Tag's gait.ts Heading (2026-10-03 to 10-06, tuned there
// on recorded matches), without its key-driven wolf extras (maxAcc, the tanh cap). It turns once a sim tick (in fixed
// sub-steps) and the frame draws between the last two ticks' facings by alpha, as positions are drawn: the same at any
// frame rate or game speed, nothing while paused, and every tick's facing is followed even when a frame covers several
// (Astra, review 2026-10-30). Numbers: scripts/turnlog.ts. Guard: tests/turn.test.ts.
import { CLASSES, type ClassId } from '../sim/defs';

/** w: the follower's stiffness (1/s); maxRate: its top turning speed (rad/s); ease: how fast the target itself is eased
 *  toward the sim's facing (1/s). */
export interface TurnSpec { w: number; maxRate: number; ease: number }

/** Per class, as each moves: a soldier on foot turns a quarter in about a third of a second; a horse takes a wider,
 *  slower turn; a cart or a ram swings round slowly; a hero is a touch quicker than his men; the giant is heavy. */
export const TURN: Record<ClassId | 'giant', TurnSpec> = {
  footman: { w: 14, maxRate: 9, ease: 30 },
  line: { w: 14, maxRate: 9, ease: 30 },
  spear: { w: 14, maxRate: 9, ease: 30 },
  ranged: { w: 14, maxRate: 9, ease: 30 },
  support: { w: 14, maxRate: 9, ease: 30 },
  elite: { w: 13, maxRate: 8, ease: 28 },
  hero: { w: 16, maxRate: 10, ease: 35 },
  cavalry: { w: 10, maxRate: 6, ease: 20 },
  siege: { w: 7, maxRate: 2.5, ease: 12 },
  ram: { w: 7, maxRate: 2.5, ease: 12 },
  giant: { w: 8, maxRate: 3, ease: 15 },
};
/** The spec drawn for a sim class index, or the giant's. */
export const turnSpec = (cls: number, giant = false) => TURN[giant ? 'giant' : CLASSES[cls]];

/** Wrap an angle to (-PI, PI]. */
export const wrap = (a: number) => a - Math.PI * 2 * Math.round(a / (Math.PI * 2));

/** The longest sub-step (s): a tick (1/20 s) turns in 12. */
const H = 1 / 240;

/** The drawn facing of every slot, by uid: a new soldier in a slot (a spawn, a revived hero, a new match) faces his own
 *  way at once instead of swinging round from the last one's. yaw is this tick's, prev the tick before's. */
export class Turns {
  readonly yaw: Float32Array; readonly prev: Float32Array; readonly vel: Float32Array; readonly tgt: Float32Array; readonly uid: Int32Array;
  constructor(cap: number) {
    this.yaw = new Float32Array(cap); this.prev = new Float32Array(cap); this.vel = new Float32Array(cap); this.tgt = new Float32Array(cap); this.uid = new Int32Array(cap);
  }
  /** Forget every slot (a new match: uids start again from 1). */
  reset() { this.uid.fill(0); }
  /** Face slot i straight at face. */
  snap(i: number, uid: number, face: number) { this.uid[i] = uid; this.yaw[i] = this.prev[i] = this.tgt[i] = wrap(face); this.vel[i] = 0; return this.yaw[i]; }
  /** Turn slot i (soldier uid) toward the sim's facing over dt seconds of game time (a tick); returns the new facing. */
  step(i: number, uid: number, face: number, dt: number, s: TurnSpec) {
    if (this.uid[i] !== uid) return this.snap(i, uid, face);
    this.prev[i] = this.yaw[i];
    if (!(dt > 0)) return this.yaw[i];
    const n = Math.ceil(dt / H - 1e-9), h = dt / n, k = 1 - Math.exp(-s.ease * h), w = s.w, R = s.maxRate;
    let a = this.yaw[i], v = this.vel[i], t = this.tgt[i];
    for (let j = 0; j < n; j++) {
      t = wrap(t + wrap(face - t) * k);
      v += (w * w * wrap(t - a) - 2 * w * v) * h;
      v = Math.min(R, Math.max(-R, v));
      a = wrap(a + v * h);
    }
    this.yaw[i] = a; this.vel[i] = v; this.tgt[i] = t;
    return a;
  }
  /** The facing to draw alpha of the way from the tick before to this one. */
  at(i: number, alpha: number) { return wrap(this.prev[i] + wrap(this.yaw[i] - this.prev[i]) * alpha); }
}
